import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../utils/numberFormatter";
import { useCmsContent } from "../hooks/useCmsContent";
import "../styles/animated-counter.css";

/**
 * Animates a number from 0 to `end` over `duration` ms.
 * Animation starts only when the element enters the viewport.
 *
 * @param {Object}  props
 * @param {number}  props.end             - Target value.
 * @param {number}  [props.duration=2000] - Animation duration in ms.
 * @param {string}  [props.prefix='']     - Text prepended to the number.
 * @param {string}  [props.suffix='']     - Text appended to the number.
 * @param {boolean} [props.useShortFormat=true] - Use K / M abbreviations.
 */
const Counter = ({
  end,
  duration = 2000,
  prefix = "",
  suffix = "",
  useShortFormat = true,
}) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 },
    );

    if (countRef.current) {
      const el = countRef.current;
      observer.observe(el);
      return () => observer.unobserve(el);
    }
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    let animationFrame;

    const updateCount = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;

      if (progress < duration) {
        setCount(Math.min(Math.round(end * (progress / duration)), end));
        animationFrame = requestAnimationFrame(updateCount);
      } else {
        setCount(end);
      }
    };

    animationFrame = requestAnimationFrame(updateCount);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, isVisible]);

  return (
    <div ref={countRef} className="counter-value">
      {prefix}
      {useShortFormat ? formatNumber(count) : count.toLocaleString()}
      {suffix}
    </div>
  );
};

/**
 * Stats section with four animated counters.
 * Numbers are driven by CMS content; hardcoded values act as the fallback
 * when the CMS is unavailable.
 */
const AnimatedStats = () => {
  const { t } = useTranslation("about");
  const { content } = useCmsContent();
  const { statsFollowers, statsViews, statsInterviews, statsIndustries } =
    content.texts;

  return (
    <section className="animated-stats-section">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>{t("stats.title")}</h2>
          <div className="title-underline"></div>
          <p>{t("stats.subtitle")}</p>
        </motion.div>

        <div className="stats-grid">
          {/* Interviews */}
          <motion.div
            className="stat-card"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="stat-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="icon"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
            <h3 className="stat-title">{t("stats.interviews")}</h3>
            <Counter end={statsInterviews} suffix="+" />
          </motion.div>

          {/* Industries */}
          <motion.div
            className="stat-card"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="stat-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="icon"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h3 className="stat-title">{t("stats.industries")}</h3>
            <Counter end={statsIndustries} suffix="+" />
          </motion.div>

          {/* Followers */}
          <motion.div
            className="stat-card"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="stat-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="icon"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <h3 className="stat-title">{t("stats.followers")}</h3>
            <Counter end={statsFollowers} suffix="+" useShortFormat={true} />
          </motion.div>

          {/* Views */}
          <motion.div
            className="stat-card"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="stat-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="icon"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            </div>
            <h3 className="stat-title">{t("stats.views")}</h3>
            <Counter end={statsViews} useShortFormat={true} />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AnimatedStats;
